import { MAX_RECORDS, MAX_STORAGE_SIZE, MINUTE, RETENTION, STORAGE_KEY } from './constants';

const safeText = (value, limit = 80) => typeof value === 'string' && value.length <= limit;
const validTime = (value, now) => Number.isFinite(value) && value > now - RETENTION && value <= now + 60000;

const mergeRecord = (previous, next) => {
  if (!previous) return next;
  if (previous.kind === 'visit')
    return { ...next, at: Math.min(previous.at, next.at), qualified: previous.qualified || next.qualified };
  if (previous.kind === 'booking') {
    if (previous.state === 'cancelled' && next.state !== 'cancelled') return previous;
    if (next.state === 'cancelled' && previous.state !== 'cancelled') return next;
  }
  return previous.at <= next.at ? next : previous;
};

const cleanRecord = (record, now) => {
  if (!record || !safeText(record.id) || !validTime(record.at, now)) return;
  if (!['attempt', 'booking', 'search', 'selection', 'rates', 'checkout', 'visit'].includes(record.kind)) return;
  const result = { id: record.id, at: record.at, kind: record.kind };
  if (record.kind === 'attempt' && typeof record.payLater === 'boolean') result.payLater = record.payLater;
  if (record.kind === 'visit') result.qualified = record.qualified === true;
  ['target', 'start', 'end', 'contact', 'hotel', 'occupation'].forEach((key) => {
    if (safeText(record[key])) result[key] = record[key];
  });
  if (Number.isInteger(record.rooms) && record.rooms > 0 && record.rooms <= 100) result.rooms = record.rooms;
  if (['pending', 'failed', 'returned', 'confirmed', 'cancelled', 'observed'].includes(record.state)) {
    result.state = record.state;
  }
  ['createdAt', 'cancelledAt'].forEach((key) => {
    if (validTime(record[key], now)) result[key] = record[key];
  });
  return result;
};

const cleanRecords = (records, now) => {
  if (!Array.isArray(records)) return [];
  const counts = {};
  const unique = new Map();
  records.slice(-MAX_RECORDS * 6).forEach((record) => {
    const clean = cleanRecord(record, now);
    if (clean) unique.set(clean.id, mergeRecord(unique.get(clean.id), clean));
  });
  return [...unique.values()]
    .sort((first, second) => first.at - second.at)
    .reverse()
    .filter((record) => {
      const limit = ['attempt', 'booking'].includes(record.kind) ? MAX_RECORDS : 16;
      counts[record.kind] = (counts[record.kind] || 0) + 1;
      return counts[record.kind] <= limit;
    })
    .reverse();
};

const createStorage = (scope, getStorage = () => window.localStorage) => {
  const key = `${STORAGE_KEY}:${scope}`;
  let available = true;

  const read = (now = Date.now()) => {
    try {
      const raw = getStorage().getItem(key);
      if (!raw) return [];
      if (raw.length > MAX_STORAGE_SIZE) throw new Error('Invalid trust state');
      const state = JSON.parse(raw);
      if (state?.version !== 1 || !Array.isArray(state.records)) throw new Error('Invalid trust version');
      return cleanRecords(state.records, now);
    } catch {
      available = false;
      return [];
    }
  };

  const write = (records, now = Date.now()) => {
    try {
      const merged = new Map(read(now).map((record) => [record.id, record]));
      cleanRecords(records, now).forEach((record) => {
        merged.set(record.id, mergeRecord(merged.get(record.id), record));
      });
      const next = cleanRecords([...merged.values()], now);
      const raw = JSON.stringify({ version: 1, records: next });
      if (raw.length > MAX_STORAGE_SIZE) throw new Error('Trust state too large');
      getStorage().setItem(key, raw);
      available = true;
      return next;
    } catch {
      available = false;
      return cleanRecords(records, now);
    }
  };

  return { key, read, write, available: () => available };
};

const createSession = (scope, getStorage = () => window.sessionStorage) => {
  const key = `${STORAGE_KEY}:${scope}:pending`;
  return {
    read: (now = Date.now()) => {
      try {
        const raw = getStorage().getItem(key);
        if (!raw || raw.length > 16384) return;
        const value = JSON.parse(raw);
        if (
          value.version !== 1 ||
          !safeText(value.id) ||
          !safeText(value.target) ||
          !safeText(value.method) ||
          !safeText(value.context, 8192) ||
          !['pending', 'confirming', 'returned', 'failed'].includes(value.state) ||
          !Number.isFinite(value.at) ||
          value.at > now ||
          value.at < now - 30 * MINUTE
        )
          return;
        return {
          id: value.id,
          at: value.at,
          target: value.target,
          method: value.method,
          context: value.context,
          state: value.state,
        };
      } catch {
        return undefined;
      }
    },
    write: (value) => {
      try {
        if (!value) getStorage().removeItem(key);
        else getStorage().setItem(key, JSON.stringify({ version: 1, ...value }));
      } catch {
        return false;
      }
      return true;
    },
  };
};

export { cleanRecords, createSession, createStorage, mergeRecord };
